import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import type { ICotizacionNBS } from '../../../modelos/CotizadorNBS/entidades/ICotizacionNBS';
import type { IConfiguracionState } from '../../configuracionCotizadorNbs/interfaces';
import { EstadoInicialConfiguracion, variablesObligatoriasCompletas } from '../../configuracionCotizadorNbs/interfaces';
import { FormularioVariablesGlobalesVista } from '../../configuracionCotizadorNbs/vista/formularioVariablesGlobalesVista';
import { BarraCambiosSinGuardarVista } from '../../configuracionCotizadorNbs/vista/barraCambiosSinGuardarVista';
import { HistorialCambiosVariablesCV } from '../../configuracionCotizadorNbs/controladorVista/historialCambiosVariablesCV';
import { CotizadorModelo } from '../../../modelos/CotizadorNBS';
import { useNotificaciones } from '../../../globales/contextos/notificationContext';
import { EnumTipoNotificacion } from '../../../globales/interfaces';
import { usePermisos } from '../../../globales/contextos/usePermisos';
import { EnumPermisos } from '../../../globales/enums/enumPermisos';
import { PAIS_PANAMA_ID } from '../interfaces';

/** Lo que la gerencia puede editar en una cotización (documento de requerimientos, 2026-10-15). */
const CAMPOS_GERENCIA: (keyof IConfiguracionState)[] = [
    'MargenImportadoAscensor', 'MargenLocalAscensor', 'MargenImportadoEscalera', 'MargenLocalEscalera',
    'DescuentoAscensor', 'DescuentoEscalera'
];

/** Márgenes y descuentos fuera de los topes de la configuración (los define el administrador; null = sin tope). */
const fueraDeTopes = (config: IConfiguracionState) => {
    const errores: string[] = [];
    const revisar = (valor: number, nombre: string, minimo: number | null, maximo: number | null) => {
        if (minimo !== null && minimo !== undefined && Number(valor) < minimo) errores.push(`${nombre} no puede ser menor que ${minimo}%.`);
        if (maximo !== null && maximo !== undefined && Number(valor) > maximo) errores.push(`${nombre} no puede ser mayor que ${maximo}%.`);
    };
    revisar(config.MargenImportadoAscensor, 'El margen importado de ascensores', config.MargenMinimo, config.MargenMaximo);
    revisar(config.MargenLocalAscensor, 'El margen local de ascensores', config.MargenMinimo, config.MargenMaximo);
    revisar(config.MargenImportadoEscalera, 'El margen importado de escaleras', config.MargenMinimo, config.MargenMaximo);
    revisar(config.MargenLocalEscalera, 'El margen local de escaleras', config.MargenMinimo, config.MargenMaximo);
    revisar(config.DescuentoAscensor, 'El descuento de ascensores', config.DescuentoMinimo, config.DescuentoMaximo);
    revisar(config.DescuentoEscalera, 'El descuento de escaleras', config.DescuentoMinimo, config.DescuentoMaximo);
    return errores;
};

interface ConfiguracionCotizacionCVProps {
    cotizacion: ICotizacionNBS;
    soloLectura: boolean;
    /** true si quedó guardado (el padre recarga la cotización). */
    alGuardar: (configuracion: IConfiguracionState) => Promise<boolean>;
    alUsarConfiguracionGlobal: () => void;
}

/**
 * Pestaña "Configuración" del Resumen: variables globales con las que se calcula esta cotización.
 * Los cambios aplican solo a esta cotización; la configuración global no cambia.
 */
export const ConfiguracionCotizacionCV: React.FC<ConfiguracionCotizacionCVProps> = ({
    cotizacion,
    soloLectura,
    alGuardar,
    alUsarConfiguracionGlobal
}) => {
    const { mostrarNotificacion } = useNotificaciones();
    const { tienePermiso } = usePermisos();
    // Administrador comercial: todo. Gerencia: márgenes y descuentos. Asesores comerciales: solo lectura, salvo
    // las pólizas en Panamá cuando la cotización las lleva (switch del paso 1).
    const puedeConfigurar = tienePermiso(EnumPermisos.PERMISO_COTIZADOR_NBS_CONFIGURAR);
    const puedeEditarMargenes = tienePermiso(EnumPermisos.PERMISO_COTIZADOR_NBS_DESCUENTOS_MARGEN);
    const puedeEditarPolizas = tienePermiso(EnumPermisos.PERMISO_COTIZADOR_NBS_EDITAR_POLIZAS)
        && cotizacion.PaisId === PAIS_PANAMA_ID && cotizacion.Proyecto.AplicaPolizas;
    const sinPermiso = !puedeConfigurar && !puedeEditarMargenes && !puedeEditarPolizas;
    const camposEditables = puedeConfigurar ? undefined : [
        ...(puedeEditarMargenes ? CAMPOS_GERENCIA : []),
        ...(puedeEditarPolizas ? ['PorcentajePolizas' as const] : [])
    ];
    const formularioSoloLectura = soloLectura || sinPermiso;

    // Márgenes, descuentos y flete marítimo también se guardan en la cotización (el cálculo usa esos):
    // se muestran los de la cotización, no los de la fila de configuración.
    const configuracionBase = useMemo<IConfiguracionState>(() => {
        const usada = cotizacion.ConfiguracionUsada ?? {};
        return {
            ...EstadoInicialConfiguracion,
            ...usada,
            MargenImportadoAscensor: cotizacion.MargenImportadoAscensores ?? usada.MargenImportadoAscensor ?? 0,
            MargenLocalAscensor: cotizacion.MargenLocalAscensores ?? usada.MargenLocalAscensor ?? 0,
            MargenImportadoEscalera: cotizacion.MargenImportadoEscaleras ?? usada.MargenImportadoEscalera ?? 0,
            MargenLocalEscalera: cotizacion.MargenLocalEscaleras ?? usada.MargenLocalEscalera ?? 0,
            DescuentoAscensor: cotizacion.DescuentoAscensores ?? usada.DescuentoAscensor ?? 0,
            DescuentoEscalera: cotizacion.DescuentoEscaleras ?? usada.DescuentoEscalera ?? 0,
            FleteMaritimoAscensor: cotizacion.FleteMaritimoAscensores ?? usada.FleteMaritimoAscensor ?? 0,
            FleteMaritimoEscalera: cotizacion.FleteMaritimoEscaleras ?? usada.FleteMaritimoEscalera ?? 0,
        };
    }, [cotizacion]);

    const [config, setConfig] = useState<IConfiguracionState>(configuracionBase);
    useEffect(() => setConfig(configuracionBase), [configuracionBase]);

    const alCambiar = (key: keyof IConfiguracionState, value: number | string | null) => {
        setConfig(prev => ({ ...prev, [key]: value === '' || value === null ? value : Number(value) } as IConfiguracionState));
    };

    const haCambiado = JSON.stringify(config) !== JSON.stringify(configuracionBase);
    const [guardando, setGuardando] = useState(false);

    const guardar = async () => {
        if (!variablesObligatoriasCompletas(config, cotizacion.UsaTrm ?? true)) {
            mostrarNotificacion(EnumTipoNotificacion.Warning, 'Campos Obligatorios', 'Debes diligenciar todos los campos obligatorios antes de guardar.');
            return;
        }
        // Topes de margen y descuento de la configuración (reemplazan el 10% fijo que había).
        const errores = fueraDeTopes(config);
        if (errores.length > 0) {
            mostrarNotificacion(EnumTipoNotificacion.Warning, 'Márgenes y descuentos', errores.join(' '));
            return;
        }
        setGuardando(true);
        try {
            await alGuardar(config);
        } finally {
            setGuardando(false);
        }
    };

    // Si la global cambió, el botón ya está en el aviso del Resumen (visible también en esta pestaña): no se repite.
    // Quien solo edita pólizas no puede volver a la global: se perderían los cambios de la gerencia.
    const puedeUsarGlobal = !formularioSoloLectura && (puedeConfigurar || puedeEditarMargenes) && cotizacion.ConfiguracionPersonalizada && !cotizacion.ConfiguracionDesactualizada;

    return (
        <Stack gap="md">
            <Group justify="space-between" align="center" wrap="wrap" gap="xs">
                {/* Sin número de versión (no le dice nada al usuario): solo si usa la global o una propia. */}
                <Group gap="xs" wrap="wrap">
                    <Badge color="primary" variant="light" radius="xl" style={{ textTransform: 'none' }}>
                        {cotizacion.ConfiguracionPersonalizada ? 'Configuración propia de esta cotización' : 'Usa la configuración global'}
                    </Badge>
                    <Text size="xs" c="dimmed">
                        {formularioSoloLectura
                            ? 'Configuración con la que se calculó esta cotización.'
                            : 'Los cambios aquí aplican solo a esta cotización; la configuración global no cambia.'}
                    </Text>
                </Group>
                <Group gap="xs">
                    {cotizacion.Id && (
                        <HistorialCambiosVariablesCV
                            subtitulo="Variables de la cotización"
                            monedaLocal={cotizacion.MonedaLocal ?? 'COP'}
                            cargar={async () => (await new CotizadorModelo().obtenerCambiosVariablesCotizacion(cotizacion.Id as string)).Data ?? []}
                        />
                    )}
                    {puedeUsarGlobal && !haCambiado && (
                        <Button variant="light" color="primary" size="xs" radius="xl" leftSection={<IconRefresh size={14} />} onClick={alUsarConfiguracionGlobal}>
                            Usar configuración global
                        </Button>
                    )}
                </Group>
            </Group>

            <FormularioVariablesGlobalesVista config={config} alCambiar={alCambiar} soloLectura={formularioSoloLectura} camposEditables={camposEditables}
                monedaLocal={cotizacion.MonedaLocal ?? 'COP'} usaTrm={cotizacion.UsaTrm ?? true} />

            {/* Aparece solo con cambios sin guardar, fija abajo: se guarda sin volver arriba. */}
            <BarraCambiosSinGuardarVista
                visible={!formularioSoloLectura && haCambiado}
                alDescartar={() => setConfig(configuracionBase)}
                alGuardar={guardar}
                textoGuardar="Guardar y recalcular"
                guardando={guardando}
            />
        </Stack>
    );
};
